import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getSalesInvoices, exportInvoiceReportToExcel } from "../../components/Api/GmsBilling";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { Eye, Filter, FileText, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  invoice_Id: null,
  customer_Id: null,
  fromDate: null,
  toDate: null,
  invoice_Type: null,
  paymentMethod: null,
};

export default function InvoiceReportPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);

  // Filter options
  const invoiceTypeOptions = [
    { value: "ca", label: "Cash" },
    { value: "cr", label: "Credit" }
  ];

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "on", label: "Online" }
  ];

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsInvoiceReport');

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  useEffect(() => {
    if (customers.length > 0) {
      loadInvoices();
    }
  }, [customers]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCustomers(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ status: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getSalesInvoices(filter);
      
      // Map customer names to invoices
      const invoicesWithCustomerNames = (data || []).map(invoice => {
        const customer = customers.find(c => c.customer_Id === invoice.invoice_CustomerId);
        return {
          ...invoice,
          customer_NameEn: customer?.customer_NameEn || 'N/A',
          customer_Phone: customer?.customer_ContactNo || 'N/A',
          customer_Email: customer?.customer_Email || 'N/A'
        };
      });
      
      setInvoices(invoicesWithCustomerNames);
      setTotalCount(invoicesWithCustomerNames.length);
    } catch (error) {
      console.error("Error loading invoices:", error);
      Swal.fire("Error", "Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    loadInvoices();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    loadInvoices();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const filterData = {
        Invoice_Id: filter.invoice_Id ? parseInt(filter.invoice_Id) : null,
        Customer_Id: filter.customer_Id,
        FromDate: filter.fromDate ? new Date(filter.fromDate).toISOString() : null,
        ToDate: filter.toDate ? new Date(filter.toDate).toISOString() : null,
        Invoice_Type: filter.invoice_Type,
        PaymentMethod: filter.paymentMethod,
        ForReturn: false
      };
      
      await exportInvoiceReportToExcel(filterData);
      Swal.fire("Success", "Invoice report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting report:", error);
      Swal.fire("Error", "Failed to export invoice report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (invoice) => {
    try {
      setViewLoadingRow(invoice.invoice_Id);
      // Navigate to BillingPage with invoice data for read-only view
      navigate('/GmsBilling', { 
        state: { 
          viewInvoice: invoice,
          isReadOnly: true 
        } 
      });
    } catch (error) {
      console.error("Error viewing invoice:", error);
      Swal.fire("Error", "Failed to load invoice details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount || 0);
  };

  const getPaginatedInvoices = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return invoices.slice(startIndex, endIndex);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Invoice Report" breadcrumbItem="Reports" />
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">
                  <FileText className="me-2" size={20} />
                  Invoice Report
                </h4>

                {/* Filter Section */}
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="d-flex align-items-center gap-2"
                  >
                    <Filter size={16} />
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleExportExcel}
                    disabled={loading || invoices.length === 0}
                    className="d-flex align-items-center gap-2"
                  >
                    <Download size={16} />
                    Export Excel
                  </Button>
                </div>

                {showFilters && (
                  <Card className="mb-3">
                    <CardBody>
                      <Row>
                                                 <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_invoice_Id">Invoice Number</Label>
                             <Input
                               id="filter_invoice_Id"
                               type="text"
                               value={filter.invoice_Id || ""}
                               onChange={(e) =>
                                 setFilter({ ...filter, invoice_Id: e.target.value || null })
                               }
                               placeholder="Search invoice number..."
                             />
                           </div>
                         </Col>
                         <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_customer_Id">Customer Name</Label>
                             <Select
                               id="filter_customer_Id"
                               value={selectedCustomerFilterOption}
                               onChange={(option) => {
                                 setSelectedCustomerFilterOption(option);
                                 setFilter({
                                   ...filter,
                                   customer_Id: option ? option.value : null,
                                 });
                               }}
                               options={customerSearchOptions}
                               isClearable
                               isSearchable
                               placeholder="Type at least 3 characters to search customers..."
                               styles={selectStyles}
                               isLoading={customerSearchLoading}
                               noOptionsMessage={() => "Type at least 3 characters to search"}
                               loadingMessage={() => "Searching customers..."}
                              onInputChange={(inputValue) => {
                                // Clear previous timeout
                                if (searchTimeout) {
                                  clearTimeout(searchTimeout);
                                }
                                
                                // Set new timeout
                                const timeout = setTimeout(() => {
                                  searchCustomersManually(inputValue);
                                }, 300);
                                setSearchTimeout(timeout);
                                
                                return inputValue;
                              }}
                             />
                           </div>
                         </Col>
                         <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_fromDate">From Date</Label>
                             <DatePicker
                               selected={filter.fromDate}
                               onChange={(date) => setFilter({ ...filter, fromDate: date })}
                               className="form-control"
                               placeholderText="Select from date"
                               dateFormat="yyyy-MM-dd"
                             />
                           </div>
                         </Col>
                         <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_toDate">To Date</Label>
                             <DatePicker
                               selected={filter.toDate}
                               onChange={(date) => setFilter({ ...filter, toDate: date })}
                               className="form-control"
                               placeholderText="Select to date"
                               dateFormat="yyyy-MM-dd"
                             />
                           </div>
                         </Col>
                      </Row>
                      <Row>
                                                 <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_invoice_Type">Invoice Type</Label>
                             <Select
                               id="filter_invoice_Type"
                               value={
                                 filter.invoice_Type
                                   ? invoiceTypeOptions.find(option => option.value === filter.invoice_Type)
                                   : null
                               }
                               onChange={(option) =>
                                 setFilter({
                                   ...filter,
                                   invoice_Type: option ? option.value : null,
                                 })
                               }
                               options={invoiceTypeOptions}
                               isClearable
                               placeholder="Select Invoice Type"
                               styles={selectStyles}
                             />
                           </div>
                         </Col>
                         <Col md={3}>
                           <div className="mb-3">
                             <Label for="filter_paymentMethod">Payment Method</Label>
                             <Select
                               id="filter_paymentMethod"
                               value={
                                 filter.paymentMethod
                                   ? paymentMethodOptions.find(option => option.value === filter.paymentMethod)
                                   : null
                               }
                               onChange={(option) =>
                                 setFilter({
                                   ...filter,
                                   paymentMethod: option ? option.value : null,
                                 })
                               }
                               options={paymentMethodOptions}
                               isClearable
                               placeholder="Select Payment Method"
                               styles={selectStyles}
                             />
                           </div>
                         </Col>
                        <Col md={6}>
                          <div className="mb-3 d-flex gap-2" style={{ marginTop: "32px" }}>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={handleApplyFilter}
                              disabled={loading}
                            >
                              Apply Filter
                            </Button>
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={handleClearFilter}
                              disabled={loading}
                            >
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Invoice List ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Invoice Number</th>
                            <th>Date</th>
                            <th>Customer Name</th>
                            <th>Invoice Type</th>
                            <th>Total Quantity</th>
                            <th>Net Amount</th>
                            <th>Payment Method</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="9" className="text-center">
                                <ClipLoader size={20} color="#556ee6" />
                                <span className="ms-2">Loading...</span>
                              </td>
                            </tr>
                          ) : invoices.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center">
                                No invoices found
                              </td>
                            </tr>
                          ) : (
                                                         getPaginatedInvoices().map((invoice, index) => (
                               <tr key={invoice.invoice_Id}>
                                 <td>{(page - 1) * pageSize + index + 1}</td>
                                 <td>{invoice.invoice_No}</td>
                                 <td>{formatDate(invoice.invoice_Date)}</td>
                                 <td>{invoice.customer_NameEn || 'N/A'}</td>
                                 <td>
                                   <span className={`badge ${invoice.invoice_Type === 'ca' ? 'bg-success' : 'bg-warning'}`}>
                                     {invoice.invoice_Type === 'ca' ? 'Cash' : 'Credit'}
                                   </span>
                                 </td>
                                 <td>{invoice.invoice_TotalQty || 0}</td>
                                 <td>{formatCurrency(invoice.invoice_NetAmount)}</td>
                                 <td>
                                   <span className={`badge ${
                                     invoice.paymentMethod === 'ca' ? 'bg-success' : 
                                     invoice.paymentMethod === 'cc' ? 'bg-info' : 'bg-primary'
                                   }`}>
                                     {invoice.paymentMethod === 'ca' ? 'Cash' : 
                                      invoice.paymentMethod === 'cc' ? 'Credit Card' : 'Online'}
                                   </span>
                                 </td>
                                 <td>
                                   <div className="d-flex gap-2">
                                     <Button
                                       color="primary"
                                       size="sm"
                                       onClick={() => handleView(invoice)}
                                       disabled={viewLoadingRow === invoice.invoice_Id}
                                     >
                                       {viewLoadingRow === invoice.invoice_Id ? (
                                         <ClipLoader size={12} color="#fff" />
                                       ) : (
                                         <Eye size={14} />
                                       )}
                                       <span className="ms-1">View</span>
                                     </Button>
                                   </div>
                                 </td>
                               </tr>
                             ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
                 </Row>
       </Container>
     </div>
   );
 }
